import Phaser from 'phaser';
import BattleStateManager from '../game/BattleStateManager.js';
import { loadVictoryMusicManifest, getCurrentTheme } from '../Utils.js';
import sessionStateManager from '../game/SessionStateManager.js';

/**
 * VictoryScene - Winner celebration scene
 * 
 * This scene displays the battle winners with celebratory effects for 20 seconds.
 * It shows winner names, trophy icons, confetti particles, and victory music.
 * 
 * @class VictoryScene
 */
export class VictoryScene extends Phaser.Scene {
  constructor() {
    super({ key: 'VictoryScene' });
    this.displayDuration = 10000; // 20 seconds
    this.startTime = null;
    this.confettiParticles = [];
    this.victoryMusicManifest = [];
    this.currentVictoryMusic = null;
    this.currentVictoryMusicKey = null;
  }

  init(data) {
    console.log('🏆 VictoryScene: init() called with data:', data);
    // Get winner data from scene data or BattleStateManager
    this.winners = data?.winners || BattleStateManager.getWinners() || [];
    this.battleResults = data?.battleResults || BattleStateManager.getBattleResults() || {};
    this.battleRewards = data?.battleRewards || BattleStateManager.getBattleRewards() || null;
    console.log('🏆 VictoryScene initialized:', this.winners);
    console.log('🏆 Battle results:', this.battleResults);
    
    console.log('🏆 VictoryScene initialized:', {
      winnerCount: this.winners.length,
      winners: this.winners.map(w => w.name || w.hero?.name || 'Unknown')
    });
  }

  async preload() {
    console.log('🏆 VictoryScene: preload() called');
    
    // Load victory music from theme
    try {
      this.victoryMusicManifest = await loadVictoryMusicManifest();
      console.log('🎵 Victory music manifest loaded:', this.victoryMusicManifest);
      
      if (this.victoryMusicManifest && this.victoryMusicManifest.length > 0) {
        // Load the first victory track
        const firstTrack = this.victoryMusicManifest[0];
        const key = firstTrack.key;
        const theme = getCurrentTheme();
        const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
        const s3BasePath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`;
        const musicPath = `${s3BasePath}/music/${key}`;
        
        console.log('🎵 Loading victory music:', musicPath);
        this.load.audio(key, musicPath);
      }
    } catch (error) {
      console.warn('Failed to load victory music manifest:', error);
    }
  }

  create() {
    console.log('🏆 VictoryScene: create() called');
    const { width, height } = this.cameras.main;
    
    // Create semi-transparent overlay to darken battlefield background
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x0F172A);
    this.background.setAlpha(0.7); // Semi-transparent to show battlefield through
    
    // Add subtle gradient overlay for better visual effect
    this.gradientOverlay = this.add.rectangle(width / 2, height / 2, width, height, 0x1E293B);
    this.gradientOverlay.setAlpha(0.2); // Very subtle overlay
    
    // Create main content container
    this.contentContainer = this.add.container(width / 2, height / 2).setDepth(10);
    
    // Create victory celebration UI
    this.createVictoryUI(width, height);
    
    // Create confetti particles
    this.createConfettiEffect(width, height);
    
    // Play victory music
    this.playVictoryMusic();
    
    // Start the display timer (delayed by 1 second for fade-in)
    this.startTime = Date.now() + 1000; // Add 1 second delay for fade-in
    console.log('🏆 VictoryScene: startTime set to', this.startTime);
    
    // Fade in animation
    this.contentContainer.setAlpha(0);
    this.tweens.add({
      targets: this.contentContainer,
      alpha: 1,
      duration: 1000,
      ease: 'Power2'
    });
  }

  createVictoryUI(width, height) {
    // Main victory container
    const victoryWidth = Math.min(900, width * 0.9);
    const victoryHeight = Math.min(700, height * 0.8);
    
    // Background panel with glow effect
    const panelBg = this.add.rectangle(0, 0, victoryWidth, victoryHeight, 0x1E293B);
    panelBg.setStrokeStyle(4, 0xF3F300);
    panelBg.setAlpha(0.95);
    this.contentContainer.add(panelBg);
    
    // Add glow effect
    const glow = this.add.rectangle(0, 0, victoryWidth + 20, victoryHeight + 20, 0xF3F300);
    glow.setAlpha(0.1);
    glow.setBlendMode(Phaser.BlendModes.ADD);
    this.contentContainer.addAt(glow, 0);
    
    // Victory title
    const title = this.add.text(0, -victoryHeight / 2 + 60, '🏆 VICTORY! 🏆', {
      fontSize: '48px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5);
    this.contentContainer.add(title);
    
    // Animate title
    this.tweens.add({
      targets: title,
      scaleX: { from: 0.8, to: 1.2 },
      scaleY: { from: 0.8, to: 1.2 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Winner section
    this.createWinnerSection(victoryWidth, victoryHeight);
    
    // Battle stats section
    this.createBattleStatsSection(victoryWidth, victoryHeight);
    
    // Create countdown progress bar at the bottom
    this.createProgressBar(victoryWidth, victoryHeight);
  }
  
  createProgressBar(containerWidth, containerHeight) {
    const barWidth = containerWidth * 0.8;
    const barHeight = 20;
    const barY = containerHeight / 2 - 40;
    
    // Background bar (full width, dark)
    this.progressBarBg = this.add.rectangle(0, barY, barWidth, barHeight, 0x1E293B);
    this.progressBarBg.setStrokeStyle(2, 0xFFFFFF);
    this.progressBarBg.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarBg);
    
    // Progress bar fill (starts full width, shrinks as time passes)
    this.progressBarFill = this.add.rectangle(-barWidth / 2, barY, barWidth, barHeight - 4, 0xF3F300);
    this.progressBarFill.setOrigin(0, 0.5);
    this.progressBarFill.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarFill);
    
    // Add glow effect to progress bar
    const progressGlow = this.add.rectangle(0, barY, barWidth, barHeight + 4, 0xF3F300);
    progressGlow.setAlpha(0); // Start invisible
    progressGlow.setBlendMode(Phaser.BlendModes.ADD);
    this.contentContainer.addAt(progressGlow, this.contentContainer.list.indexOf(this.progressBarBg));
    
    // Fade in progress bar after 1 second
    this.time.delayedCall(1000, () => {
      this.tweens.add({
        targets: [this.progressBarBg, this.progressBarFill, progressGlow],
        alpha: { from: 0, to: 1 },
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          // Set final alpha values
          this.progressBarBg.setAlpha(0.8);
          this.progressBarFill.setAlpha(0.9);
          progressGlow.setAlpha(0.2);
        }
      });
    });
  }

  createWinnerSection(victoryWidth, victoryHeight) {
    const winnerY = -victoryHeight / 2 + 150;
    
    if (this.winners.length === 0) {
      // No winners - show default message
      const noWinnerText = this.add.text(0, winnerY, 'Battle Complete', {
        fontSize: '32px',
        color: '#E2E8F0',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      this.contentContainer.add(noWinnerText);
      return;
    }
    
    if (this.winners.length === 1) {
      // Single winner
      const winner = this.winners[0];
      const winnerName = winner.name || winner.hero?.name || 'Unknown Hero';
      
      // Winner sprite (large and animated)
      const winnerSprite = this.createWinnerSprite(winner, 0, winnerY + 220, 0.75);
      this.contentContainer.add(winnerSprite);
      
      // Winner name with crown (animated)
      const crownIcon = this.add.image(0, winnerY + 200, 'crown');
      crownIcon.setScale(0.25);
      crownIcon.setAlpha(0);
      this.contentContainer.add(crownIcon);
      
      // Animate crown: pop in, grow large, then shrink and fade
      this.tweens.add({
        targets: crownIcon,
        alpha: 1,
        scaleX: 2,
        scaleY: 2,
        duration: 800,
        ease: 'Back.easeOut',
        onComplete: () => {
          // After growing, shrink and fade out
          this.tweens.add({
            targets: crownIcon,
            alpha: 0,
            scaleX: 0.8,
            scaleY: 0.8,
            duration: 1200,
            ease: 'Power2.easeIn',
            delay: 500 // Hold the large size for a moment
          });
        }
      });
      
      const winnerText = this.add.text(0, winnerY, winnerName, {
        fontSize: '36px',
        color: '#F3F300',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5);
      this.contentContainer.add(winnerText);
      
      // Animate winner text
      this.tweens.add({
        targets: winnerText,
        scaleX: { from: 1, to: 1.1 },
        scaleY: { from: 1, to: 1.1 },
        duration: 1500,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
    } else {
      // Multiple winners
      const winnerText = this.add.text(0, winnerY, '🏆 Multiple Winners! 🏆', {
        fontSize: '32px',
        color: '#F3F300',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5);
      this.contentContainer.add(winnerText);
      
      // List winners with sprites
      const winnerListY = winnerY + 60;
      this.winners.forEach((winner, index) => {
        const winnerName = winner.name || winner.hero?.name || 'Unknown Hero';
        
        // Winner sprite (smaller for multiple winners)
        const winnerSprite = this.createWinnerSprite(winner, -200, winnerListY + (index * 60), 0.6);
        this.contentContainer.add(winnerSprite);
        
        const medalIcon = this.add.image(-150, winnerListY + (index * 60), 'medal');
        medalIcon.setScale(0.6);
        this.contentContainer.add(medalIcon);
        
        const winnerNameText = this.add.text(-100, winnerListY + (index * 60), `${index + 1}. ${winnerName}`, {
          fontSize: '24px',
          color: '#E2E8F0',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold'
        }).setOrigin(0, 0.5);
        this.contentContainer.add(winnerNameText);
      });
    }
  }

  createWinnerSprite(winner, x, y, scale = 1.2) {
    // Try to get the player's texture key
    const textureKey = `player_${winner.hero?.id || winner.wallet || winner.id}`;
    
    let sprite;
    
    // Check if we have a custom texture for this player
    if (this.textures.exists(textureKey)) {
      sprite = this.add.image(x, y, textureKey);
      console.log('🏆 Using custom texture for winner:', textureKey);
    } else {
      // Fallback to theme player textures or default
      const theme = getCurrentTheme();
      if (theme && theme.players && theme.players.length > 0) {
        // Use a random theme player texture as fallback
        const randomPlayer = theme.players[Math.floor(Math.random() * theme.players.length)];
        const fallbackKey = typeof randomPlayer === 'string' ? randomPlayer : (randomPlayer.image || randomPlayer.asset || '');
        const safeFallbackKey = String(fallbackKey || '').replace(/\.[^/.]+$/, '');
        
        if (this.textures.exists(safeFallbackKey)) {
          sprite = this.add.image(x, y, safeFallbackKey);
          console.log('🏆 Using theme fallback texture for winner:', safeFallbackKey);
        } else {
          // Ultimate fallback - create a colored rectangle with initials
          sprite = this.createFallbackSprite(winner, x, y);
          console.log('🏆 Using fallback sprite for winner:', winner.name);
        }
      } else {
        // Ultimate fallback - create a colored rectangle with initials
        sprite = this.createFallbackSprite(winner, x, y);
        console.log('🏆 Using fallback sprite for winner:', winner.name);
      }
    }
    
    // Set scale and add glow effect
    sprite.setScale(scale);
    sprite.setDepth(5);
    
    // Add glow effect
    const glow = this.add.image(x, y, sprite.texture.key);
    glow.setScale(scale * 1.2);
    glow.setAlpha(0.3);
    glow.setTint(0xF3F300); // Golden glow
    glow.setBlendMode(Phaser.BlendModes.ADD);
    glow.setDepth(4);
    
    // Add to content container
    this.contentContainer.add(glow);
    this.contentContainer.add(sprite);
    
    // Animate the sprite
    this.tweens.add({
      targets: sprite,
      scaleX: { from: scale * 0.8, to: scale * 1.0 },
      scaleY: { from: scale * 0.8, to: scale * 1.0 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Animate the glow
    this.tweens.add({
      targets: glow,
      alpha: { from: 0.1, to: 0.4 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Add floating animation
    this.tweens.add({
      targets: [sprite, glow],
      y: { from: y - 5, to: y + 5 },
      duration: 3000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    return sprite;
  }

  createFallbackSprite(winner, x, y) {
    // Create a colored rectangle with player initials
    const winnerName = winner.name || winner.hero?.name || 'Unknown Hero';
    const initials = winnerName.substring(0, 2).toUpperCase();
    
    // Create a colored rectangle
    const graphics = this.add.graphics();
    graphics.fillStyle(0x4F46E5); // Indigo color
    graphics.fillRoundedRect(-30, -30, 60, 60, 10);
    graphics.generateTexture(`fallback_${winner.id}`, 60, 60);
    graphics.destroy();
    
    const sprite = this.add.image(x, y, `fallback_${winner.id}`);
    
    // Add initials text
    const initialsText = this.add.text(x, y, initials, {
      fontSize: '20px',
      color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    this.contentContainer.add(initialsText);
    
    return sprite;
  }

  createBattleStatsSection(victoryWidth, victoryHeight) {
    const statsY = victoryHeight / 2 - 120;
    
    // Battle duration
    const duration = this.battleResults.duration || 'Unknown';
    const durationText = this.add.text(-victoryWidth / 2 + 50, statsY, `⏱️ Duration: ${duration}`, {
      fontSize: '18px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5);
    this.contentContainer.add(durationText);
    
    // Total eliminations
    const eliminations = this.battleResults.totalEliminations || 0;
    const eliminationsText = this.add.text(victoryWidth / 2 - 50, statsY, `⚔️ Eliminations: ${eliminations}`, {
      fontSize: '18px',
      color: '#EF4444',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(1, 0.5);
    this.contentContainer.add(eliminationsText);
    
    // Battle mode
    const gameMode = this.battleResults.gameMode || 'Battle Royale';
    const modeText = this.add.text(0, statsY + 30, `🎮 Mode: ${gameMode}`, {
      fontSize: '18px',
      color: '#8B5CF6',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.contentContainer.add(modeText);
  }

  createConfettiEffect(width, height) {
    // Create confetti particles
    const confettiCount = 1000;
    
    for (let i = 0; i < confettiCount; i++) {
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(-10000, -2500);
      
      const confetti = this.add.image(x, y, 'confetti');
      confetti.setScale(Phaser.Math.FloatBetween(0.5, 1.5));
      confetti.setTint(Phaser.Math.Between(0x000000, 0xFFFFFF));
      confetti.setRotation(Phaser.Math.FloatBetween(0, Math.PI * 2));
      
      // Animate confetti falling
      this.tweens.add({
        targets: confetti,
        y: height + 100,
        rotation: confetti.rotation + Math.PI * 4,
        duration: Phaser.Math.Between(3000, 12000),
        ease: 'Power2',
        onComplete: () => {
          // Reset confetti position for continuous effect
          confetti.y = Phaser.Math.Between(-100, -50);
          confetti.x = Phaser.Math.Between(0, width);
        }
      });
      
      this.confettiParticles.push(confetti);
    }
  }

  playVictoryMusic() {
    try {
      const victoryKey = 'music-victory'; // Fixed key from JoinScene preloading
      
      // Check if victory music is preloaded
      if (this.cache.audio && this.cache.audio.exists(victoryKey)) {
        const music = this.sound.play(victoryKey, { volume: 0.6, loop: true });
        this.currentVictoryMusic = music;
        this.currentVictoryMusicKey = victoryKey;
        console.log('🎵 Victory music started:', victoryKey);
      } else {
        console.warn('Victory music not preloaded, attempting fallback load');
        this.loadVictoryMusicFallback();
      }
    } catch (error) {
      console.warn('Could not play victory music:', error);
    }
  }

  loadVictoryMusicFallback() {
    // Fallback: try to load from manifest if preloading failed
    if (this.victoryMusicManifest && this.victoryMusicManifest.length > 0) {
      const firstTrack = this.victoryMusicManifest[0];
      const key = firstTrack.key;
      const theme = getCurrentTheme();
      const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
      const s3BasePath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`;
      const musicPath = `${s3BasePath}/music/${key}`;
      
      console.log('🎵 Loading victory music fallback:', musicPath);
      
      this.load.audio(key, musicPath);
      this.load.once('complete', () => {
        if (this.sound.get(key)) {
          const music = this.sound.play(key, { volume: 0.6, loop: true });
          this.currentVictoryMusic = music;
          this.currentVictoryMusicKey = key;
          console.log('🎵 Victory music started (fallback):', firstTrack.title || key);
        }
      });
      this.load.start();
    } else {
      console.log('🎵 No victory music available in theme');
    }
  }

  update(time, delta) {
    if (!this.startTime) {
      console.log('🏆 VictoryScene: startTime not set, skipping update');
      return;
    }
    
    const elapsed = Date.now() - this.startTime;
    const remaining = Math.max(0, this.displayDuration - elapsed);
    const progress = Math.max(0, Math.min(1, remaining / this.displayDuration));
    const remainingSeconds = Math.ceil(remaining / 1000);
    
    // Update progress bar
    if (this.progressBarFill && this.progressBarBg) {
      const barWidth = this.progressBarBg.width;
      const newWidth = barWidth * progress;
      this.progressBarFill.width = newWidth;
      this.progressBarFill.x = -barWidth / 2;
      
      // Change color based on remaining time
      if (remainingSeconds <= 3) {
        this.progressBarFill.setFillStyle(0xEF4444); // Red
      } else if (remainingSeconds <= 6) {
        this.progressBarFill.setFillStyle(0xF59E0B); // Orange
      } else {
        this.progressBarFill.setFillStyle(0xF3F300); // Yellow
      }
    }
    
    // Transition to FirstTimeScene when time is up
    if (remaining <= 0) {
      console.log('👋 Transitioning to FirstTimeScene...');
      this.scene.start('FirstTimeScene', {
        battleResults: this.battleResults,
        winners: this.winners,
        allPlayers: this.battleResults?.playerRankings?.map(ranking => ranking.player) || [],
        battleRewards: this.battleRewards
      });
    }
  }

  shutdown() {
    // Stop victory music
    try {
      if (this.currentVictoryMusic && this.currentVictoryMusicKey) {
        this.sound.stopByKey(this.currentVictoryMusicKey);
        console.log('🎵 Victory music stopped:', this.currentVictoryMusicKey);
      }
    } catch (error) {
      console.warn('Could not stop victory music:', error);
    }
    
    // Clean up confetti particles
    this.confettiParticles.forEach(confetti => {
      if (confetti && confetti.active) {
        confetti.destroy();
      }
    });
    this.confettiParticles = [];
    
    // Shut down GameScene when leaving VictoryScene
    this.shutdownGameScene();
    
    console.log('🏆 VictoryScene shutdown');
  }
  
  shutdownGameScene() {
    // Shut down GameScene completely when leaving VictoryScene
    const gameScene = this.scene.get('GameScene');
    if (gameScene) {
      console.log('🎮 VictoryScene: Shutting down GameScene');
      gameScene.scene.stop();
    }
  }
}
